import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, ChevronLeft, ChevronRight, Loader2, CalendarDays, Users, MapPin } from "lucide-react";
import { loadPannellum } from "@/components/PanoramaViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gbp = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v || 0);

/** Interactive 360 pane embedded in the lightbox — finger/mouse drag works natively via Pannellum. */
const PanoPane = ({ url }) => {
  const ref = useRef(null);
  const viewerRef = useRef(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pannellum = await loadPannellum();
        if (cancelled || !ref.current) return;
        viewerRef.current = pannellum.viewer(ref.current, {
          type: "equirectangular",
          panorama: url,
          autoLoad: true,
          autoRotate: -2,
          compass: false,
          mouseZoom: true,
          hfov: 100,
          showControls: false,
        });
        setReady(true);
      } catch { /* leave the spinner off, photos remain available */ }
    })();
    return () => { cancelled = true; try { viewerRef.current?.destroy?.(); } catch { /* noop */ } };
  }, [url]);
  return (
    <div className="absolute inset-0" data-testid="lightbox-pano">
      <div ref={ref} className="absolute inset-0" />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center text-[#F5EFE6]">
          <Loader2 className="animate-spin" size={26} />
        </div>
      )}
    </div>
  );
};

/**
 * PitchLightbox — full-screen photo gallery for one pitch/lodge.
 * Swipe (finger drag, arrows, dots or keyboard) through every photo; a 360°
 * pane when a panorama exists. The bottom tile mirrors the hero CTA: what
 * you're booking (unit, dates, guests) + live price + Book now, which hands
 * off to the booking page with the pitch locked in.
 */
const PitchLightbox = ({ unit, media, startDate, endDate, guests, nights, available, initialMode, onClose, onBook }) => {
  const images = media?.images || [];
  const pano = media?.panorama_url || "";
  const [idx, setIdx] = useState(0);
  const [mode, setMode] = useState(initialMode === "360" && pano ? "360" : "photos");
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(true);
  const touchX = useRef(null);
  const isLodge = unit?.sub_type === "lodge";

  // live price for the booking tile
  useEffect(() => {
    let cancelled = false;
    setQuoteLoading(true);
    axios
      .post(`${API}/quote`, {
        unit_type: isLodge ? "lodge" : "touring",
        unit_id: unit.id,
        start_date: startDate,
        end_date: endDate,
        guests,
      })
      .then((r) => { if (!cancelled) setQuote(r.data); })
      .catch(() => { if (!cancelled) setQuote(null); })
      .finally(() => { if (!cancelled) setQuoteLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit?.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (mode === "photos") {
        if (e.key === "ArrowRight") setIdx((i) => Math.min(images.length - 1, i + 1));
        if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, mode, images.length]);

  const prev = () => setIdx((i) => Math.max(0, i - 1));
  const next = () => setIdx((i) => Math.min(images.length - 1, i + 1));

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col" role="dialog" aria-modal="true" data-testid="pitch-lightbox">
      {/* top bar — name + photos/360 switch + close */}
      <div className="flex items-center justify-between px-4 py-3 text-[#F5EFE6] gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#B76E79] shrink-0">{isLodge ? "Lodge" : "Pitch"}</span>
          <span className="text-sm truncate" data-testid="lightbox-title">{unit.name}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {pano && (
            <div className="inline-flex rounded-full border border-white/20 overflow-hidden">
              <button type="button" onClick={() => setMode("photos")} className={`px-3 py-1.5 text-[9px] uppercase tracking-[0.12em] transition ${mode === "photos" ? "bg-[#F5EFE6] text-[#0F0E0E]" : "text-[#F5EFE6]/70 hover:text-white"}`} data-testid="lightbox-mode-photos">Photos</button>
              <button type="button" onClick={() => setMode("360")} className={`px-3 py-1.5 text-[9px] uppercase tracking-[0.12em] transition ${mode === "360" ? "bg-[#B76E79] text-white" : "text-[#F5EFE6]/70 hover:text-white"}`} data-testid="lightbox-mode-360">360°</button>
            </div>
          )}
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-white/10" aria-label="Close gallery" data-testid="lightbox-close"><X size={20} /></button>
        </div>
      </div>

      {/* main stage — swipeable photos or the 360 pane */}
      <div
        className="flex-1 relative overflow-hidden"
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchX.current == null || mode !== "photos") return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          if (dx > 50) prev(); else if (dx < -50) next();
          touchX.current = null;
        }}
      >
        {mode === "360" && pano ? (
          <PanoPane url={pano} />
        ) : images.length ? (
          <img key={idx} src={images[idx]} alt={`${unit.name} — photo ${idx + 1} of ${images.length}`} className="absolute inset-0 w-full h-full object-contain" draggable={false} data-testid="lightbox-image" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#F5EFE6]/60 text-sm">No photos yet for this spot.</div>
        )}
        {mode === "photos" && images.length > 1 && (
          <>
            <button type="button" onClick={prev} disabled={idx === 0} className="absolute left-3 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-black/55 text-white hover:bg-[#B76E79] transition disabled:opacity-30" aria-label="Previous photo" data-testid="lightbox-prev"><ChevronLeft size={20} /></button>
            <button type="button" onClick={next} disabled={idx === images.length - 1} className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-black/55 text-white hover:bg-[#B76E79] transition disabled:opacity-30" aria-label="Next photo" data-testid="lightbox-next"><ChevronRight size={20} /></button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, i) => (
                <button key={i} type="button" onClick={() => setIdx(i)} className={`w-2 h-2 rounded-full transition ${i === idx ? "bg-[#B76E79] scale-125" : "bg-white/40"}`} aria-label={`Photo ${i + 1}`} data-testid={`lightbox-dot-${i}`} />
              ))}
            </div>
          </>
        )}
        {mode === "360" && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-[0.18em] text-white/70 bg-black/50 px-3 py-1 rounded-full pointer-events-none">
            Drag to look around
          </div>
        )}
      </div>

      {/* booking tile — mirrors the hero "Book now" CTA */}
      <div className="border-t border-white/10 bg-[#0F0E0E] px-4 py-3.5" data-testid="lightbox-booking-tile">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="text-[#F5EFE6] min-w-0">
            <div className="font-serif text-lg leading-tight truncate">{unit.name}</div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[11px] text-[#F5EFE6]/70">
              {unit.lake && <span className="inline-flex items-center gap-1"><MapPin size={11} className="text-[#B76E79]" />{unit.lake}</span>}
              <span className="inline-flex items-center gap-1"><CalendarDays size={11} className="text-[#B76E79]" />{startDate} → {endDate} · {nights} night{nights !== 1 ? "s" : ""}</span>
              <span className="inline-flex items-center gap-1"><Users size={11} className="text-[#B76E79]" />{guests} guest{guests !== 1 ? "s" : ""}</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              {quoteLoading ? (
                <span className="text-[#F5EFE6]/60 text-xs inline-flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Pricing…</span>
              ) : quote ? (
                <>
                  <div className="font-serif text-2xl text-[#F5EFE6]" data-testid="lightbox-price">{gbp(quote.total)}</div>
                  <div className="text-[10px] text-[#F5EFE6]/60">{quote.payment_type === "deposit" ? `${gbp(quote.payable_now)} deposit today` : "pay in full today"}</div>
                </>
              ) : (
                <div className="font-serif text-xl text-[#F5EFE6]">{gbp(unit.nightly)}<span className="text-xs text-[#F5EFE6]/60"> /night</span></div>
              )}
            </div>
            {available ? (
              <button type="button" onClick={() => onBook?.(unit.id)} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#B76E79] text-white hover:bg-[#8C4E58] transition text-[11px] font-brand tracking-[0.18em] uppercase" data-testid="lightbox-book-btn">
                Book now
              </button>
            ) : (
              <span className="text-[11px] text-[#F5EFE6]/60 uppercase tracking-[0.12em]" data-testid="lightbox-unavailable">Taken for these dates</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PitchLightbox;
